import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Sun, Moon, Menu, X, LogOut, Trophy, ChevronLeft, Home, Shield, LogIn, Volume2, VolumeX, Award, Gift, Pencil } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import { useSiteContent } from '../contexts/SiteContentContext';
import { useCMSVisibility } from '../context/CMSVisibilityContext';
import LanguageSelector from './LanguageSelector';
import { CreditBalance } from './CreditBalance';
import { useCredits } from '../context/CreditContext';
import { useSoundsMuted } from '../hooks/useGameSounds';
import CertNameEditor from './CertNameEditor';

export default function UnifiedNavbar({
  user = null,
  showBack = false,
  backLink = '/dashboard',
  backLabel = 'Back',
  pageTitle = '',
  siteName = 'Ikizamini',
  logoUrl = null,
  showLogo = true,
  onSignInClick = null,
  onVerifyClick = null,
  // Optional ReactNode rendered immediately after the page title — used by
  // pages that want to surface a slim status indicator (e.g. a mastery
  // progress bar) right next to the title rather than inside their own
  // content area. Hidden on small screens where the title itself is
  // already truncated to keep the navbar uncluttered.
  pageTitleExtra = null,
  // Optional override: pages can supply a custom function that runs when
  // the back button is clicked (e.g. CreateQuiz pops to its type-picker
  // step instead of navigating to a route).
  onBackClick = null,
  // Optional content rendered inside the fixed wrapper, ABOVE the nav row
  // (used by the homepage to slot in announcement banners that grow/shrink
  // and naturally push the nav row down without a layout jump).
  topSlot = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { content: siteContent } = useSiteContent();
  const { isGlobalVisible } = useCMSVisibility();
  const { onLogout, isAuthenticated, user: contextUser } = useCredits();
  const { muted: soundsMuted, toggle: toggleSoundsMuted } = useSoundsMuted();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // OTP-gated name editor — opens from the "Edit name" link in the
  // hamburger user-info row. Renders an absolute modal at component root.
  const [showNameEditor, setShowNameEditor] = useState(false);

  // Prefer the explicit `user` prop (legacy callers) but fall back to the
  // signed-in user from CreditContext so the hamburger avatar/name show
  // up everywhere automatically — pages no longer need to thread the
  // user through MainLayout.
  const currentUser = user || contextUser;

  // Initials fallback when the user has no profile picture (typical for
  // phone-OTP signups).
  const initials = (currentUser?.name || currentUser?.email || currentUser?.phone || '')
    .trim()
    .split(/\s+/)
    .map(part => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

  const handleLogout = async () => {
    try {
      await onLogout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  const displayName = siteContent?.site_name || siteName;
  const displayLogo = siteContent?.logo_url || logoUrl;

  return (
    <>
      {/* The fixed wrapper holds an optional `topSlot` (e.g. banners) that
          can grow/shrink in height; the actual <nav> row sits below it in
          flow so it gets pushed down naturally — no `top` jump. */}
      <div className="fixed top-0 left-0 right-0 z-50">
        {topSlot}
        <nav className={`border-b backdrop-blur-sm ${
          isDark ? 'bg-gray-900/95 border-gray-800' : 'bg-[#0d4a8a] border-white/10'
        }`}>
        {/* Grid layout so the left cell (back/logo), center cell (title),
            and right cell (controls) always line up at the same x-positions
            regardless of page — identical feel across every route. */}
        <div
          className="px-4 md:px-8 lg:px-12 py-3 max-w-7xl mx-auto grid items-center"
          style={{ gridTemplateColumns: '1fr auto 1fr', minHeight: 56 }}
        >
          {/* Left cell: optional back button (only for pages that are
              logical children of another page) + brand logo + page name
              (light, after a thin separator). Empty center cell so the
              right-side controls have breathing room. */}
          <div className="flex items-center gap-2 justify-self-start min-w-0">
            {showBack && (backLink !== '/' || onBackClick) && (
              <button
                onClick={() => onBackClick ? onBackClick() : navigate(backLink)}
                data-testid="nav-back-btn"
                className={`flex items-center justify-center rounded-lg p-1.5 transition-colors flex-shrink-0 ${
                  isDark
                    ? 'text-white hover:bg-gray-800'
                    : 'text-white hover:bg-white/15'
                }`}
                aria-label={backLabel || 'Back'}
                title={backLabel || 'Back'}
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
              </button>
            )}
            <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="nav-left-logo">
              {/* Reserved 24×24 box for the brand logo so the brand text
                  doesn't shift horizontally when siteContent finally loads
                  a logo URL. Stays empty (visible space) until ready. */}
              {showLogo !== false && (
                <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 24, height: 24 }} aria-hidden={!displayLogo}>
                  {displayLogo && <img src={displayLogo} alt="Logo" width={24} height={24} className="w-6 h-6 object-contain" />}
                </span>
              )}
              <span className="brand-logo text-xl font-bold text-white truncate">{displayName}</span>
            </Link>
            {/* Page name as light secondary label after a thin vertical bar.
                Only rendered when the page has explicitly supplied one — and
                we keep it from competing with the brand by using a light
                font weight, lower opacity, and a small text size. */}
            {pageTitle && (
              <span
                className="hidden sm:flex items-center gap-2 min-w-0"
                data-testid="nav-page-title"
              >
                <span className="text-white/30 select-none" aria-hidden="true">|</span>
                <span className="text-sm font-light text-white/85 truncate">{pageTitle}</span>
                {pageTitleExtra && (
                  <span className="hidden md:flex items-center" data-testid="nav-page-title-extra">
                    {pageTitleExtra}
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Center cell: intentionally empty by design — page name lives in
              the left cell next to the brand. */}
          <div className="justify-self-center" aria-hidden="true" />

          {/* Right cell: only the Language selector + Hamburger menu
              remain visible per design. Everything else (Sign In /
              Credits, Mute, Home, Leaderboard, Verify, Theme, Logout)
              lives inside the hamburger across all screen sizes. */}
          <div className="flex items-center gap-2 justify-self-end">
            {/* Language Selector — always visible. The persistent navbar
                background is brand blue in light mode, so we tell the
                selector to render white-on-blue via `onDarkBg`. */}
            <LanguageSelector compact onDarkBg={!isDark} />

            {/* Hamburger Menu — visible at every breakpoint (per design).
                The negative right margin (`-mr-2`) offsets the button's
                inner padding so the menu icon visually aligns with the
                same right-edge as the brand-logo text on the left, giving
                the navbar symmetric outer margins. */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className={`-mr-2 p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-800' : 'hover:bg-white/15'
              }`}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              {showMobileMenu ? (
                <X className="w-5 h-5 text-white" />
              ) : (
                <Menu className="w-5 h-5 text-white" />
              )}
            </button>
          </div>
        </div>
      </nav>
      </div>

      {/* Hamburger menu — visible at all breakpoints, per design. Renders
          as a fixed dropdown anchored to the right side of the navbar. */}
      {showMobileMenu && (
        <>
          {/* Backdrop closes the menu on outside click. */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setShowMobileMenu(false)}
            aria-hidden="true"
          />
          <div
            className={`fixed top-[57px] right-2 sm:right-4 z-40 w-72 rounded-xl border shadow-2xl overflow-hidden ${
              isDark ? 'bg-gray-900 border-gray-800' : 'bg-[#0a3d73] border-white/15'
            }`}
            data-testid="hamburger-menu"
          >
            <div className="px-2 py-2 space-y-0.5">
              {/* User Info — shown when signed in (CreditContext provides
                  the profile, so every page gets it automatically). */}
              {currentUser && (
                <div
                  className={`flex items-center gap-3 p-3 rounded-lg mb-1 ${
                    isDark ? 'bg-gray-800' : 'bg-white/10'
                  }`}
                  data-testid="hamburger-user-info"
                >
                  {currentUser.picture ? (
                    <img
                      src={currentUser.picture}
                      alt={currentUser.name || 'Account'}
                      width={40}
                      height={40}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-brand-400 to-brand-600 text-white text-sm font-semibold"
                      aria-hidden="true"
                    >
                      {initials}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white truncate" data-testid="hamburger-user-name">
                      {currentUser.name || 'Account'}
                    </div>
                    {(currentUser.email || currentUser.phone) && (
                      <div className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-white/60'}`} data-testid="hamburger-user-contact">
                        {currentUser.email || currentUser.phone}
                      </div>
                    )}
                    {/* Edit name — opens an OTP-gated modal. Reuses the
                        same CertNameEditor widget used in the certificate
                        preview, hitting `PUT /me/profile-name` with a
                        fresh verification_token. */}
                    <button
                      type="button"
                      onClick={() => { setShowMobileMenu(false); setShowNameEditor(true); }}
                      className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium ${
                        isDark ? 'text-brand-300 hover:text-brand-200' : 'text-white/80 hover:text-white'
                      }`}
                      data-testid="menu-edit-name-btn"
                    >
                      <Pencil className="w-3 h-3" /> Edit name
                    </button>
                  </div>
                </div>
              )}

              {/* Sign In — when not authenticated and the caller has
                  provided an onSignInClick handler. Moved out of the
                  navbar per design (Feb 2026) so the only visible
                  controls are the language selector + hamburger. */}
              {!isAuthenticated && onSignInClick && (
                <button
                  onClick={() => { onSignInClick(); setShowMobileMenu(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold ${
                    isDark ? 'hover:bg-gray-800 text-brand-300' : 'hover:bg-white/10 text-white'
                  }`}
                  data-testid="menu-sign-in-btn"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </button>
              )}

              {/* Credits — when authenticated. Renders the same compact
                  pill the navbar used to host, but as a full-width row so
                  it lays out neatly inside the dropdown. */}
              {isAuthenticated && (
                <div
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                    isDark ? 'bg-gray-800/60' : 'bg-white/10'
                  }`}
                  data-testid="menu-credits-row"
                >
                  <CreditBalance compact />
                </div>
              )}

              {/* Home — when not already on /. */}
              {location.pathname !== '/' && (
                <button
                  onClick={() => { navigate('/'); setShowMobileMenu(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    isDark ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-white/10 text-white/95'
                  }`}
                  data-testid="menu-home-btn"
                >
                  <Home className="w-4 h-4" />
                  Home
                </button>
              )}

              {/* Leaderboard - if not on leaderboard page */}
              {location.pathname !== '/leaderboard' && (
                <button
                  onClick={() => { navigate('/leaderboard'); setShowMobileMenu(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    isDark ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-white/10 text-white/95'
                  }`}
                  data-testid="menu-leaderboard-btn"
                >
                  <Trophy className="w-4 h-4" />
                  Leaderboard
                </button>
              )}

              {/* My Certificates — signed-in users only. Surfaces every
                  certificate the user has earned across topics in one
                  place (the dedicated /certificates page). */}
              {isAuthenticated && location.pathname !== '/certificates' && (
                <button
                  onClick={() => { navigate('/certificates'); setShowMobileMenu(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    isDark ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-white/10 text-white/95'
                  }`}
                  data-testid="menu-certificates-btn"
                >
                  <Award className="w-4 h-4" />
                  My Certificates
                </button>
              )}

              {/* My Rewards — Hongbao prizes won across games. Signed-in
                  users only since rewards are tied to a user account. */}
              {isAuthenticated && location.pathname !== '/rewards' && (
                <button
                  onClick={() => { navigate('/rewards'); setShowMobileMenu(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    isDark ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-white/10 text-white/95'
                  }`}
                  data-testid="menu-rewards-btn"
                >
                  <Gift className="w-4 h-4" />
                  My Rewards
                </button>
              )}

              {/* Sound mute toggle — kept in the menu (not the navbar
                  row) so the navbar stays minimal. Closing the menu after
                  the toggle would force a 2-tap to mute → unmute, so we
                  intentionally keep it open. */}
              <button
                onClick={toggleSoundsMuted}
                aria-pressed={soundsMuted}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                  isDark ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-white/10 text-white/95'
                }`}
                data-testid="menu-sound-toggle"
              >
                {soundsMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                {soundsMuted ? 'Unmute sounds' : 'Mute sounds'}
              </button>

              {/* Theme Toggle */}
              {isGlobalVisible('theme_toggle') && (
                <button
                  onClick={() => { toggleTheme(); setShowMobileMenu(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    isDark ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-white/10 text-white/95'
                  }`}
                  data-testid="menu-theme-btn"
                >
                  {isDark ? <Sun className="w-4 h-4 text-yellow-400" /> : <Moon className="w-4 h-4" />}
                  {isDark ? 'Light Mode' : 'Dark Mode'}
                </button>
              )}

              {/* Verify Certificate */}
              {onVerifyClick && (
                <button
                  onClick={() => { onVerifyClick(); setShowMobileMenu(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    isDark ? 'hover:bg-gray-800 text-gray-200' : 'hover:bg-white/10 text-white/95'
                  }`}
                  data-testid="menu-verify-btn"
                >
                  <Shield className="w-4 h-4" />
                  Verify Certificate
                </button>
              )}

              {/* Divider */}
              <div className={`my-1 border-t ${isDark ? 'border-gray-800' : 'border-white/10'}`} />

              {/* Logout — only when actually signed in. */}
              {isAuthenticated && (
                <button
                  onClick={() => { handleLogout(); setShowMobileMenu(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-400 ${
                    isDark ? 'hover:bg-gray-800' : 'hover:bg-white/10'
                  }`}
                  data-testid="menu-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* OTP-gated name editor modal — reuses the certificate name-fix
          widget. Backend: POST /me/phone/send-otp → /verify-otp → PUT
          /me/profile-name with the returned verification_token. */}
      {showNameEditor && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowNameEditor(false)}>
          <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CertNameEditor
              isDark={isDark}
              onClose={() => setShowNameEditor(false)}
              onSaved={() => setShowNameEditor(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
